import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  InputLabel,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { CustomInput } from "global/components";
import EditIcon from "assets/icons1/EditIcon.svg";
import { useEffect, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import tag_icon from "../../../../assets/icons1/tag_icon.svg";
import CustomAutoComplete from "global/components/CustomAutoComplete/CustomAutoComplete";
import {
  cleanInput,
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
  timeZones,
} from "helpers/methods";
import {
  deleteTagByContactById,
  fetchContactById,
  fetchTagsByContactById,
  updateContactProfile,
} from "../ViewContactService";
import { useParams } from "react-router-dom";
import AddUpdateTag from "screens/Tags/Components/AddUpdateTag";
import { hasAccessTo } from "layouts/Config";
import strings from "global/constants/StringConstants";
import { CustomContactNumberInput } from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import { useAppSelector } from "utils/hooks";
import { selectIsFirstNameMandatory } from "redux/generalSlice";
import CustomInfoTooltip from "global/components/CustomInfoTooltip/CustomInfoTooltip";

const MAX_VISIBLE_CHIPS = 20;

const Profile = () => {
  const { id } = useParams<{ id: string }>();
  const isFirstNameMandatory = useAppSelector(selectIsFirstNameMandatory);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tagLoading, setTagLoading] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [showAll, setShowAll] = useState(false);
  const [contact, setContact] = useState<any>();
  const [tags, setTags] = useState<any>();
  const [openAddTagModal, setOpenAddTagModal] = useState<boolean>(false);
  const handleToggle = () => setShowAll(!showAll);
  const isContactUnsubscribed = contact?.status === 0;
  const hasContactUpdatePermission = hasAccessTo(
    strings.contact,
    strings.updatePermission
  );

  const fields = [
    {
      label: "First Name",
      key: "firstName",
      placeHolder: "Enter first name",
      required: isFirstNameMandatory,
    },
    { label: "Last Name", key: "lastName", placeHolder: "Enter last name" },
    { label: "Email Id", key: "emailId", disabled: true },
    {
      label: "Contact Number",
      key: "contactNo",
      placeHolder: "Enter contact number",
    },
    { label: "Address", key: "address", placeHolder: "Enter address" },
    {
      label: "Postal Code",
      key: "postalCode",
      placeHolder: "Enter postal code",
      limit: 20,
    },
    { label: "City", key: "city", placeHolder: "Enter city" },
    { label: "State", key: "state", placeHolder: "Enter state" },
    { label: "Country", key: "country", placeHolder: "Enter county" },
    { label: "Time Zone", key: "timezone", customInput: false },
  ];

  useEffect(() => {
    if (id) {
      getContactData();
      getTags();
    }
  }, []);

  const getContactData = async () => {
    try {
      setIsLoading(true);
      const response = await fetchContactById(+id);
      setContact(response);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const getTags = async () => {
    try {
      setTagLoading(true);
      const res = await fetchTagsByContactById(+id);
      setTags(res);
      setTagLoading(false);
    } catch (error: any) {
      setTagLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleRemoveTag = async (tagId: number) => {
    try {
      setTagLoading(true);
      const res = await deleteTagByContactById(+id, +tagId);
      openSuccessNotification(res.message);
      setTagLoading(false);
      await getTags();
    } catch (error: any) {
      setTagLoading(false);
      handleErrorMessage(error);
    }
  };

  const updateContactProfileHandler = async () => {
    try {
      if (isFirstNameMandatory && !contact.firstName) {
        return;
      }
      setIsLoading(true);
      const body: Record<string, any> = { id: +id };
      fields.forEach((field) => {
        if (contact?.[field.key] !== undefined) {
          body[field.key] = contact[field.key];
        }
      });
      const response = await updateContactProfile(body);
      openSuccessNotification(response.message);
      setIsLoading(false);
      await getContactData();
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleInputChange = (key: string, value: string) => {
    const cleanedValue = typeof value === "string" ? cleanInput(value) : value;
    setContact((prevState: any) => ({
      ...prevState,
      [key]: cleanedValue,
    }));
  };

  const getProfileDetails = () => {
    return (
      <Card sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ flexShrink: 0 }}>
            Profile Details
          </Typography>
          <CustomInfoTooltip
            infoMessage={
              hasContactUpdatePermission
                ? ""
                : "You don't have permission to edit contact details"
            }
            placement="top"
          >
            <span>
              {" "}
              <IconButton
                disabled={
                  !hasContactUpdatePermission ||
                  isLoading ||
                  isContactUnsubscribed
                }
                onClick={() => setIsEdit(!isEdit)}
              >
                <img src={EditIcon} alt="Edit Icon" />
              </IconButton>
            </span>
          </CustomInfoTooltip>
        </Box>
        <Grid container spacing={2}>
          {isLoading ? (
            [...Array(8)].map((_, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Skeleton animation="wave" variant="text" height={25} />
              </Grid>
            ))
          ) : (
            <>
              {fields.map(
                (field) =>
                  field?.customInput !== false && (
                    <Grid item xs={12} sm={6} key={field.key}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        sx={{
                          pb: 0.5,
                        }}
                      >
                        <InputLabel
                          sx={{
                            color: "#475569",
                            fontSize: "14px",
                            flexShrink: 0,
                            fontWeight: 500,
                            mr: 2,
                            minWidth: "110px",
                          }}
                          required={field.required || false}
                        >
                          {field.label}
                        </InputLabel>
                        {field.key === "contactNo" ? (
                          <CustomContactNumberInput
                            id="contactNumber"
                            value={contact?.[field.key]}
                            placeHolder="Enter Your Contact No"
                            onChange={(phone: any) => {
                              setContact((prevState: any) => ({
                                ...prevState,
                                contactNo: phone,
                              }));
                            }}
                            disabled={field.disabled || !isEdit}
                            flagDisabled={field.disabled || !isEdit}
                          />
                        ) : (
                          <CustomInput
                            placeHolder={field.placeHolder || field.label}
                            value={contact?.[field.key]}
                            disabled={field.disabled || !isEdit}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => handleInputChange(field.key, e.target.value)}
                            propsToInputElement={{
                              minLength: 1,
                              maxLength: field.limit || 100,
                            }}
                            error={
                              field.required && !isTruthy(contact?.[field.key])
                            }
                            helperText={
                              field.required && !isTruthy(contact?.[field.key])
                                ? "Enter first name"
                                : ""
                            }
                          />
                        )}
                      </Box>
                    </Grid>
                  )
              )}
              <Grid item xs={12} sm={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  sx={{
                    pb: 0.5,
                  }}
                >
                  <InputLabel
                    sx={{
                      color: "#475569",
                      fontSize: "14px",
                      flexShrink: 0,
                      fontWeight: 500,
                      mr: 2,
                      minWidth: "110px",
                    }}
                  >
                    Time Zone
                  </InputLabel>
                  <CustomAutoComplete
                    id="group-autocomplete"
                    placeHolder="Select timezone"
                    textFieldVariant="outlined"
                    value={contact?.timezone}
                    clearIcon={false}
                    options={timeZones.map((timeZone: any) => ({
                      label: timeZone.label,
                    }))}
                    onChange={(event: any, value: { label: string } | null) => {
                      const timeZoneValue = value ? value.label : "";
                      handleInputChange("timezone", timeZoneValue);
                    }}
                    disabled={!isEdit}
                  />
                </Box>
              </Grid>
              <Stack
                display="flex"
                justifyContent="end"
                alignItems="end"
                width="100%"
                pt={1.5}
              >
                <Button
                  id="save_btn"
                  variant="contained"
                  sx={{
                    width: "7.125rem",
                    height: "2.5rem",
                  }}
                  disabled={!isEdit || !hasContactUpdatePermission}
                  onClick={() => updateContactProfileHandler()}
                >
                  Save
                </Button>
              </Stack>
            </>
          )}
        </Grid>
      </Card>
    );
  };
  const getTagSection = () => {
    const remainingCount = tags?.length - MAX_VISIBLE_CHIPS;
    const visibleItems = showAll ? tags : tags?.slice(0, MAX_VISIBLE_CHIPS);
    const hasTags = tags && tags.length > 0;

    return (
      <Card sx={{ p: 2.5 }}>
        <Stack
          spacing={2}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" sx={{ flexShrink: 0 }}>
              Tags
            </Typography>{" "}
            <CustomInfoTooltip
              placement="bottom"
              infoMessage={
                "Categorize and organize contacts by adding relevant tags for better segmentation and filtering."
              }
            />
          </Stack>

          <CustomInfoTooltip
            infoMessage={
              hasContactUpdatePermission
                ? ""
                : "You don't have permission to add tags"
            }
            placement="top"
          >
            <span>
              <Button
                id="add_tag_btn"
                variant="text"
                size="small"
                startIcon={
                  <Stack
                    sx={{
                      opacity:
                        isLoading ||
                        !hasContactUpdatePermission ||
                        isContactUnsubscribed
                          ? 0.5
                          : 1,
                    }}
                  >
                    <img src={tag_icon} alt="Add_Tag icon" />
                  </Stack>
                }
                disabled={
                  isLoading ||
                  !hasContactUpdatePermission ||
                  isContactUnsubscribed
                }
                onClick={() => setOpenAddTagModal(true)}
                sx={{
                  px: "0px !important",
                }}
              >
                Add Tag
              </Button>
            </span>
          </CustomInfoTooltip>
        </Stack>

        {tagLoading ? (
          <Grid container spacing={2} display="flex" pt={1}>
            {[...Array(4)].map((_, index) => (
              <Grid item xs={12} sm={2} key={index} pr={2}>
                <Skeleton animation="wave" variant="text" height={25} />
              </Grid>
            ))}
          </Grid>
        ) : hasTags ? (
          <Grid container spacing={2} display="flex" pt={1}>
            {visibleItems?.map((tag: any) => (
              <Grid item key={tag.id} pl={0} pr={2}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    backgroundColor: "#F4F1FC",
                    p: { xs: 1, md: 1.5 },
                    borderRadius: "5px",
                  }}
                >
                  <Tooltip title={tag.name} arrow placement="top">
                    <Typography variant="subtitle1" color="#7551D0">
                      {tag.name.length > 10
                        ? `${tag.name.slice(0, 10)}...`
                        : tag.name}
                    </Typography>
                  </Tooltip>
                  <IconButton
                    sx={{ p: 0 }}
                    onClick={() => handleRemoveTag(tag.id)}
                  >
                    <CloseRoundedIcon htmlColor="#7551D0" fontSize="small" />
                  </IconButton>
                </Stack>
              </Grid>
            ))}
            {!showAll && remainingCount > 0 && (
              <Grid item display="flex" alignItems="center">
                <Typography
                  variant="body2"
                  onClick={handleToggle}
                  sx={{ cursor: "pointer" }}
                >
                  {`+${remainingCount}`} Tags
                </Typography>
              </Grid>
            )}
          </Grid>
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100px", width: "100%", textAlign: "center" }}
          >
            <Typography variant="body1">
              Precision starts with the perfect tag! 🎯
            </Typography>
          </Stack>
        )}
      </Card>
    );
  };

  const getInsightsAndTags = () => {
    return (
      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} lg={12} xl={12}>
          {getTagSection()}
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      {getProfileDetails()}
      {getInsightsAndTags()}
      {openAddTagModal && (
        <AddUpdateTag
          contactId={contact?.id}
          isVisible={openAddTagModal}
          handleDialogClose={() => setOpenAddTagModal(false)}
          callBack={() => getTags()}
          isEdit={false}
        />
      )}
    </>
  );
};

export default Profile;
